import { AdminUser } from "../redux/features/user/types";
import { PendingRequestAction, PendingRequestOperation } from "./LostStatuses";
import { PrematchedFoundItems } from "./PreMatchedFoundItems";
import { ShippingOption } from "./ShipTypes";

export interface TicketDocument {
  documentName: string;
  fullUrl: string;
  id: string;
}

export interface item {
  categoryName?: string;
  subCategoryName?: string
}

export interface Ticket {
  id?: string; // Added for better identification.
  trackingId: string; // Tracking ID of the ticket.
  status: number; // Status code of the ticket.
  lostDate: string; // Date when the item was lost.
  lostTime?: string; // Time of day (morning/afternoon/evening)
  reviewedAt?: string;
  lfNotifiedAt?: string;
  deletedAt?: string;
  firstName: string; // First name associated with the ticket, "NA" if not available.
  lastName: string; // Last name associated with the ticket, "NA" if not available.
  categoryName?: string | null; // Name of the category, null if not available.
  subCategoryName?: string | null; // Name of the sub-category, null if not available.
  createdAt: string; // Ticket creation timestamp.
  documents: number;
  items?: item[];
  isUrgent: boolean;
  isOverdue?: boolean;
  overdueAt?: string;
  statusUpdatedAt?: string;
  foundBy?: string;
  foundDate?: string;
  isPendingRequest: boolean;
  requestType?: PendingRequestOperation;
  itemNumber?: string;
  isEmailCommunication?: boolean;
  isSticky?: boolean;
  isFoundItem?: boolean;
  location?: string;
  ship?: string;
  airport?: string;
  caseManagerName?: string;
  foundType?: number;
  isPaymentSuccess: boolean;
  paidAmount?: number;
  paidCurrency?: string;
  planName?: string;
  paymentDate?: string;
  // Refund signals — populated when the customer triggered a refund on a previously
  // succeeded payment. Used to swap the kanban "Recently paid" tooltip for a stronger
  // "Refund requested" warning.
  isRefunded?: boolean;
  refundedAt?: string;
  isDraft: boolean; // True if this is a draft, false if submitted
  shippingOptions: ShippingOption[];
  hasShippingProof: boolean;
  deliveryDate: string;
  establishmentTypeIcon?: string;
  establishmentType: string;
  lfNotificationAnswer?: string;
  lfNotificationCode?: string;
  estAnswers?: Record<string, { code?: string; name?: string }>;
  digitalSignature?: string;
  idDocumentUrl?: string;
  idDocumentFilename?: string;
  signatureCompletedAt?: string;
  countryCode?: string; // Country code for displaying flag
  deletionSource?: 'manual' | 'auto-unpaid' | 'auto-draft';
}

export interface ILocationQuestionAnswer {
  id: string;
  answer: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface ILocationQuestion {
  id: string;
  fieldName: string;
  placeholder: string;
  fieldType: number;
  isRequired: boolean;
  options: string[] | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  answers: ILocationQuestionAnswer;
}

export interface Location {
  id: string;
  name: string;
  status: number;
  questions?: ILocationQuestion[];
}

export type Ship = {
  id: string;
  name: string;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  status?: string | null | number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  newClaimNotification: number;
  readyToShipNotification: number;
  foundItemDiscardNotification: number;
};

export type Airport = {
  id: string;
  name: string;
  status?: string | null | number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type Contact = {
  id: string;
  name: string;
  title?: string;
  department?: string;
  email?: string;
  phone?: string;
  mobilePhone?: string;
  fax?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
  website?: string;
  lostAndFoundFormLink?: string;
  notes?: string;
  isPrimary: boolean;
  isLFNotified?: boolean;
  index?: number;
  establishmentLocationId?: string;
  placeId?: string;
  status: number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type Place = {
  id: string;
  name: string;
  description?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
  phone?: string;
  email?: string;
  icon?: any;
  fullUrl?: string;
  index?: number;
  establishmentLocationId: string;
  status: number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  contacts?: Contact[];
};

export type EstablishmentLocation = {
  id: string;
  name: string;
  code?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
  phone?: string;
  email?: string;
  icon?: any;
  fullUrl?: string;
  slug?: string;
  specificType?: string;
  index?: number;
  establishmentTypeId: string;
  establishmentTypeIds?: string[];
  status: number;
  showInFooter?: boolean;
  footerOrder?: number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  places?: Place[];
  contacts?: Contact[];
  establishmentType?: EstablishmentType;
  establishmentTypes?: EstablishmentType[];
};

export type SpecificType = {
  name: string;
  isQuestionType: boolean;
};

export type EstablishmentType = {
  id: string;
  name: string;
  icon: any;
  fullUrl?: any;
  specificTypes?: SpecificType[];
  status: number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  questions: EstablishmentTypeQuestion[];
  establishments?: EstablishmentLocation[];
  translationKey?: string | null;
  nameTranslations?: Record<string, string> | string | null;
};

export type EstablishmentTypeQuestion = {
  id: string;
  fieldName: string;
  fieldType: number;
  placeholder?: string;
  isRequired: boolean;
  questionType?: string;
  options: Option[] | any[] | null;
  dynamicOptions?: EstablishmentLocation[];
  isLFNotified?: boolean;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  answers: Answer;
  EstablishmentType: EstablishmentType;
  hasSubQuestion?: boolean;
};

export type Country = {
  id: number;
  name: string;
  iso2: string;
  iso3: string;
  phoneCode: string;
  currency: string;
  currencyName: string;
  currencySymbol: string;
  region: string;
  regionId: string;
  subRegion: string;
  subRegionId: string;
  nationality: string;
  timezones: Array<{
    zoneName: string;
    gmtOffset: number;
    gmtOffsetName: string;
    abbreviation: string;
    tzName: string;
  }>;
  createdAt: string;
  updatedAt: string;
};

export type State = {
  id: number;
  name: string;
  countryId: number;
  stateCode: string;
  latitude: string;
  longitude: string;
  createdAt: string;
  updatedAt: string;
};

export type City = {
  id: number;
  name: string;
  stateId: number;
  countryId: number;
  latitude: string;
  longitude: string;
  createdAt: string;
  updatedAt: string;
};

export type User = {
  id: string;
  email: string;
  email2: string;
  internalEmail: string;
  firstName: string;
  lastName: string;
  zip: string;
  address1: string;
  address2: string;
  createdAt: string;
  updatedAt: string;
  city: string;
  state: string;
  country: string;
  phone: string;
  phone2: string;
  countryCode: string;
  stateCode: string;
  placeId: string;
};

export type EditReportLocationInput = {
  departurePort: string;
  departureDate: string;
  debarkPort: string;
  debarkDate: string;
  ship: string;
  location: string;
  whereDescription: string;
};

export type Document = {
  id: string | number;
  documentName: string;
  docFor: string;
  createdAt: string;
  updatedAt: string;
  fullUrl: string;
  isDeleted?: boolean;
};

export type Answer = {
  id: string;
  answer: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export interface SubQuestion {
  id: string;
  fieldName: string;
  placeholder: string;
  fieldType: number;
  isRequired: boolean;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  options: any[]; // Can be empty array or could have options depending on the type
}

export interface Option {
  id: string;
  name: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  subQuestion?: SubQuestion[]; // SubQuestion under each option, if applicable
}

export type Question = {
  placeholder?: string;
  id: string;
  fieldName: string;
  translationKey?: string | null;
  fieldNameTranslations?: Record<string, string> | null;
  placeholderTranslations?: Record<string, string> | null;
  fieldType: number;
  isRequired: boolean;
  options: Option[] | any[] | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  answers: Answer;
  subCategory: SubCategory;
  hasSubQuestion?: boolean;
};

export type Features = {
  id: string;
  name: string;
  key: string;
  description: string | null | undefined;
  translationKey?: string | null;
  nameTranslations?: Record<string, string> | null;
  descriptionTranslations?: Record<string, string> | null;
  index: number | null;
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export type PricingPlan = {
  id: string;
  title: string;
  translationKey?: string | null;
  titleTranslations?: Record<string, string> | null;
  descriptionTranslations?: Record<string, string> | null;
  amount: number;
  days: number;
  features: Features[];
  featureValues?: Record<string, string>;
  emailTemplates?: Record<string, string>;
  description?: string | null;
  isActive?: boolean;
};

export type CustomerDetails = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  lastReminderAt: string | null;
  lastReminderType: string | null;
  trackingIds: string[];
  createdAt: string;
  updatedAt: string;
  hasOverduePayment?: boolean;
  hasPaid?: boolean;
  hasRefundedPayment?: boolean;
  draftIncompleteCount: number;
  draftCompletedCount: number;
  notPaidCount: number;
  overdueClaimCount: number;
  refundedClaimCount: number;
  establishmentTypes: string[];
};

export type Category = {
  id: string;
  name: string;
  translationKey?: string | null;
  nameTranslations?: Record<string, string> | null;
  status: number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type SubCategory = {
  id: string;
  name: string;
  translationKey?: string | null;
  nameTranslations?: Record<string, string> | null;
  status: number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  questions: Question[];
  category: Category;
};

export type Item = {
  id: string;
  itemDescription: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  category: Category;
  subCategory: SubCategory;
  documents: Document[];
  lostDate?: string;
  isNew?: boolean;
};

export const PaymentStatus = {
  NotInitialized: 0,
  Pending: 1,
  Processing: 2,
  Canceled: 3,
  Succeeded: 4,
  Failed: 5,
  Refunded: 6,
};
export type PaymentStatusType = typeof PaymentStatus[keyof typeof PaymentStatus];

export const SubscriptionStatus = {
  Active: 1,
  Expired: 0,
  Cancelled: 2,
} as const;
export type SubscriptionStatusType = typeof SubscriptionStatus[keyof typeof SubscriptionStatus];

export const SubscriptionStatusLabel: Record<SubscriptionStatusType, string> = {
  [SubscriptionStatus.Active]: "Active",
  [SubscriptionStatus.Expired]: "Expired",
  [SubscriptionStatus.Cancelled]: "Cancelled",
};

export type Subscription = {
  id: string;
  status: SubscriptionStatusType;
  activatedAt?: string;
  expiresAt?: string;
  canceledAt?: string | null;
  pricingPlan?: PricingPlan | string;
};

export interface Payment {
  id: string;
  insuranceQuoteId: string;
  insuranceTransactionId: string;
  insuredValue: number;
  insuranceCharge: number;
  administrativeFee: number;
  isInsurance: boolean;
  totalPayableAmount: number;
  paymentIntentId: string;
  piClientSecret: string;
  ip: string | null;
  browser: string | null;
  status: number;
  paymentGateway: number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  shippingOption: ShippingOption;
  subscription?: Subscription | null;
}

export type DonatedDoc = {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  documentName: string;
  fullUrl: string;
};

export type Report = {
  id: string;
  travelDate: string;
  whereDescription: string;
  itemDescription: string;
  lostDate: string;
  lostTime?: string | null;
  documentDescription: string;
  trackingId: string;
  status: number;
  createdAt: string;
  updatedAt: string;
  user: User;
  airport: Airport;
  establishmentType: EstablishmentType,
  establishmentLocation?: EstablishmentLocation,
  location: Location;
  category: Category;
  subCategory: SubCategory;
  documents: Document[];
  items: Item[];
  isUrgent: boolean;
  foundItem: PrematchedFoundItems | null; // Null value here, could be any type depending on the scenario.
  shippingOptions: ShippingOption[]; // Empty array, could be populated based on actual options.
  donatedDoc: DonatedDoc; // Null value here, could be any type depending on the scenario.
  foundBy?: string;
  foundDate?: string;
  pendingRequest?: PendingRequest;
  discardReason?: string;
  isPendingRequest?: boolean;
  requestType?: PendingRequestOperation;
  itemNumber?: string;
  versionCount?: number;
  isEmailCommunication?: boolean;
  isFoundItem?: boolean;
  caseManagerName?: string;
  caseManager?: AdminUser;
  isBillingInfoUpdated?: boolean;
  payment: Payment[];
  shippingLabel?: Document;
  additionalDocs?: Document[];
  shippingProof?: Document[];
  lostReport: Document[];
  paymentReceipt?: Document[];
  trackingNumber: string;
  isPaymentSuccess: boolean;
  deletedAt?: string | null;
  bookingNumber: string;
  isCreate?: boolean;
  foundItemId?: string;
  deliveryDate?: string;
  remarks?: string;
  digitalSignature?: string;
  idDocumentUrl?: string;
  idDocumentFilename?: string;
  signatureCompletedAt?: string;
  establishmentName?: string;
  lfDepartmentName?: string;
  lfContactPerson?: string;
  lfContactEmail?: string;
  lfContactPhone?: string;
  lfNotes?: string;
  lfNotifiedAt?: string;
  additionalComments?: string;
  lfNotifications?: Array<{
    establishmentTypeId?: string;
    specificType?: string;
    establishmentLocationId?: string;
    establishmentName?: string;
    contactId?: string;
    lfDepartmentName?: string;
    lfContactPerson?: string;
    lfContactEmail?: string;
    lfContactPhone?: string;
    lfNotes?: string;
    additionalComments?: string;
    adminComment?: string;
  }>;
  visitorInfo?: any;
};

export type PendingRequest = {
  id: string;
  operation: PendingRequestOperation;
  status: PendingRequestAction;
  entityName: string;
  requestForId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  data: Report;
};

export type ToggleEmailCommunication = {
  isEmailCommunication: boolean;
};

export type Sticky = {
  id: string;
  content: string;
  status: number;
  lost: string;
  found: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
};

export enum FilterOperator {
  NOT_EQUAL_TO = "neq",
  LESS_THAN = "lt",
}

export type ShipFromPort = {
  id: string;
  contactName: string;
  status: number;
  port: string;
  country: string;
  state: string;
  address1: string;
  address2: string;
  city: string;
  stateCode: string;
  countryCode: string;
  placeId: string;
  zip: string;
  phone: string;
  email: string;
  comment: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export enum ActivationStatus {
  Active = 1,
  Inactive = 0,
}

export const PaymentGateway = {
  // Stripe: 1,
  // Authorize: 2,
  Paypal: 3,
};
export const PaymentGatewayLabel = {
  // [PaymentGateway.Stripe]: "Stripe",
  // [PaymentGateway.Authorize]: "Authorize",
  [PaymentGateway.Paypal]: "Paypal",
};

export const MeasurementType = {
  Imperial: 1,
  Metric: 2,
}
